<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              :class="(!isVerticalMenuCollapsed || isMouseHovered) ? 'navbar-brand' : ''"
              to="/dashboard"
            >
              <!-- <span
                v-if="!isVerticalMenuCollapsed || isMouseHovered"
                class="brand-logo"
              >
                <b-img
                  :src="appLogoImage"
                  alt="Udara360 logo"
                />
              </span> -->
              <!-- <span
                v-else
                class="brand-logo d-flex align-items-center justify-content-center"
              >
                <b-img
                  :src="appLogoImageSm"
                  alt="Udara360 logo"
                  height="45%"
                  width="35%"
                />
              </span> -->
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <div
        v-if="!isVerticalMenuCollapsed || isMouseHovered"
        class="ml-2 mr-2"
      >
        <!--filter -->
        <div class="menu-search-wrapper">
          <feather-icon
            icon="SearchIcon"
            size="16"
            class="menu-search-icon"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="form-control menu-search-input"
            :placeholder="$t('Search')"
          >
          <button
            v-if="searchQuery"
            type="button"
            class="menu-search-clear"
            @click="searchQuery = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>
      <!--filter -->
      </div>

      <!-- Favourites Section - Collapsible Group (matches VerticalNavMenuGroup structure) -->
      <ul
        v-if="sidebarFavorites.length > 0 && (!isVerticalMenuCollapsed || isMouseHovered)"
        class="navigation navigation-main favorites-section"
      >
        <li
          class="nav-item has-sub favorites-group"
          :class="{ 'open': isFavoritesOpen, 'sidebar-group-active': isFavoriteRouteActive }"
        >
          <b-link
            class="d-flex align-items-center"
            @click="isFavoritesOpen = !isFavoritesOpen"
          >
            <feather-icon
              icon="StarIcon"
              class="favorites-icon"
            />
            <span class="menu-title text-truncate">{{ $t('Favourites') }}</span>
          </b-link>
          <b-collapse
            v-model="isFavoritesOpen"
            class="menu-content sub-items"
            tag="ul"
          >
            <li
              v-for="favorite in sidebarFavorites"
              :key="favorite.route"
              class="nav-item favorite-item"
              :class="{ 'active': $route.name === favorite.route }"
            >
              <!-- Remove star icon - appears on hover -->
              <span
                v-b-tooltip.hover.right="$t('Remove from Favourites')"
                class="favorite-remove-trigger"
                @click.stop="removeFavoriteItem(favorite)"
              >
                <feather-icon
                  icon="StarIcon"
                  class="favorite-remove-icon"
                  size="14"
                />
              </span>
              <b-link
                :to="{ name: favorite.route }"
                class="d-flex align-items-center"
                @click.native="onFavoriteClick"
              >
                <feather-icon
                  icon="StarIcon"
                  class="favorite-star-filled"
                />
                <span class="menu-title text-truncate">{{ $t(favorite.name || favorite.title) }}</span>
              </b-link>
            </li>
          </b-collapse>
        </li>
      </ul>
      <!-- Favourites Separator -->
      <div
        v-if="sidebarFavorites.length > 0 && (!isVerticalMenuCollapsed || isMouseHovered)"
        class="favorites-separator"
      />

      <vertical-nav-menu-items
        :items="filteredMenuArray"
        class="navigation navigation-main"
        @toggle-favorite="handleFavoriteToggle"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { $themeConfig } from '@themeConfig'
import { computed, provide, ref } from '@vue/composition-api'
import { BCollapse, BLink, VBTooltip } from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { mapGetters } from 'vuex'
import navMenuItems from '@/navigation/vertical'
import FavoriteService, { MAX_FAVORITES } from '@/helpers/favoriteService'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BCollapse,
    // BImg,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      searchQuery: '',
      sidebarFavorites: [],
      isFavoritesOpen: false, // Collapsed by default
      FavoriteService,
      navigatedFromFavorite: false,
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
    }),
    isFavoriteRouteActive() {
      // Check if current route matches any favorite
      return this.sidebarFavorites.some(fav => this.$route.name === fav.route)
    },
    sideNavItems() {
      const filterItemsByFeature = items => items.reduce((filtered, item) => {
        if (item.enableFeature === true) {
          if (item.children && item.children.length > 0) {
            const children = filterItemsByFeature(item.children)
            if (children.length > 0) {
              filtered.push({ ...item, children })
            }
          } else {
            filtered.push(item)
          }
        }
        return filtered
      }, [])

      const features = filterItemsByFeature(navMenuItems)
      // const features = this.filterItemsByFeature(navMenuItems)
      const data = this.appsAndPages(features)
      const orderMenuItem = [...data].reduce((items, currentValue) => {
        if (currentValue.title === 'Dashboard') {
          // currentValue.children.push({
          //   title: 'Dashboard',
          //   route: 'dashboard',
          // })
          // currentValue.children.push({
          //   title: 'Home',
          //   route: 'home',
          // })
          return [currentValue, ...items]
        }
        return [...items, currentValue]
      }, [])
      return orderMenuItem
    },
    // ToDo: to refactor this function
    filteredMenuArray() {
      if (!this.searchQuery) {
        return this.sideNavItems
      }

      const data = []
      const elementData = []

      this.sideNavItems.forEach(sideNavItem => {
        sideNavItem.children.forEach(child => {
          if (child.title.toLowerCase().includes(this.searchQuery.toLowerCase())) {
            const da = data.find(x => x.title === sideNavItem.title)
            const a = {
              title: child.title,
              route: child.route,
              hasPermission: child.hasPermission,
            }

            if (da) {
              da.children = [...da.children, a]
            } else {
              data.push({
                title: sideNavItem.title,
                icon: sideNavItem.icon,
                children: [a],
              })
            }
          }

          if (child.children) {
            child.children.forEach(nestedChild => {
              if (nestedChild.title.toLowerCase().includes(this.searchQuery.toLowerCase())) {
                const elementDatafind = elementData.find(x => x.title === sideNavItem.title)
                const ba = {
                  title: nestedChild.title,
                  route: nestedChild.route,
                  hasPermission: nestedChild.hasPermission,
                }

                if (elementDatafind) {
                  if (!elementDatafind.children.children) {
                    elementDatafind.children.children = []
                  }
                  elementDatafind.children.children = [...elementDatafind.children.children, ba]
                } else {
                  elementData.push({
                    title: sideNavItem.title,
                    icon: sideNavItem.icon,
                    children: [
                      {
                        title: child.title,
                        route: child.route,
                        hasPermission: child.hasPermission,
                        children: [ba],
                      },
                    ],
                  })
                }
              }
            })
          }
        })
      })

      const finalData = data.filter(da => !elementData.some(ed => ed.title === da.title))

      return finalData.concat(elementData)
    },
  },
  watch: {
    $route() {
      if (this.navigatedFromFavorite) {
        // Came from favourites — keep favourites open, let the flag suppress nav group auto-open
        // Reset after all composition watchers have flushed (double nextTick)
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.favoriteNavActive = false
            this.navigatedFromFavorite = false
          })
        })
      } else {
        // Came from regular nav group — close favourites
        this.isFavoritesOpen = false
      }
    },
  },
  mounted() {
    this.loadFavorites()
    this.unsubscribeFavorites = FavoriteService.subscribe(() => {
      this.loadFavorites()
    })
  },
  beforeDestroy() {
    if (this.unsubscribeFavorites) {
      this.unsubscribeFavorites()
    }
  },
  methods: {
    loadFavorites() {
      const permissions = this.userData ? this.userData.permissions : []
      this.sidebarFavorites = FavoriteService.getPermittedFavorites(permissions).slice(0, 10)
      // Update reactive count
      this.favoritesCount = FavoriteService.getFavoritesCount()
    },
    handleFavoriteToggle(item, isCurrentlyFavorite) {
      if (isCurrentlyFavorite) {
        // Remove from favorites
        FavoriteService.removeFavorite(item.route)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Removed from Favourites',
            icon: 'StarIcon',
            variant: 'warning',
          },
        })
      } else {
        // Check if limit reached
        if (FavoriteService.isLimitReached()) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Maximum Favourites Reached',
              text: `You can only have up to ${MAX_FAVORITES} favourites. Please remove one before adding more.`,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
          return
        }
        // Add to favorites
        const success = FavoriteService.addFavorite({
          name: item.name || item.title,
          title: item.title || item.name,
          route: item.route,
          icon: item.icon,
          hasPermission: item.hasPermission,
        })
        if (success) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Added to Favourites',
              icon: 'StarIcon',
              variant: 'success',
            },
          })
        }
      }
    },
    onFavoriteClick() {
      // Tell nav groups not to auto-open; cleared by the $route watcher after watchers flush
      this.favoriteNavActive = true
      this.navigatedFromFavorite = true
    },
    removeFavoriteItem(favorite) {
      FavoriteService.removeFavorite(favorite.route)
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Removed from Favourites',
          icon: 'StarIcon',
          variant: 'warning',
        },
      })
    },
    appsAndPages(array) {
      if (this.userData !== null) {
        const getChildren = (result, object) => {
          if (this.userData.permissions.includes(object.hasPermission)) {
            result.push(object)
            return result.sort((a, b) => a.title.localeCompare(b.title))
          }
          if (Array.isArray(object.children)) {
            const children = object.children.reduce(getChildren, [])
            if (children.length) result.push({ ...object, children: children.sort((a, b) => a.title.localeCompare(b.title)) })
          }

          return result.sort((a, b) => a.title.localeCompare(b.title))
        }

        return array.reduce(getChildren, [])
      }
      return []
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    // Flag to suppress nav group auto-open when navigating via favourites
    const favoriteNavActive = ref(false)
    provide('favoriteNavActive', favoriteNavActive)

    // Favorites count - reactive ref updated by component
    const favoritesCount = ref(FavoriteService.getFavoritesCount())
    provide('favoritesCount', favoritesCount)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage, appLogoImageSm } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Favourites count
      favoritesCount,

      // Favourite navigation flag
      favoriteNavActive,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      appLogoImageSm,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.menu-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.menu-search-icon {
  position: absolute;
  left: 10px;
  color: #999;
  pointer-events: none;
  z-index: 2;
}

.menu-search-input {
  padding-left: 34px !important;
  padding-right: 34px !important;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }
}

.menu-search-clear {
  position: absolute;
  right: 6px;
  width: 22px;
  height: 22px;
  border: none;
  background: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  padding: 0;
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    background: #dee2e6;
    color: #333;
  }
}

.dark-layout {
  .menu-search-clear {
    background: #3b4253;
    color: #b4b7bd;

    &:hover {
      background: #4b5366;
      color: #fff;
    }
  }
}

.brand-logo-sm {
  width: 70px;
  height: 70px
}

// Favourites Section Styles - Matches Dashboard/Accounts groups exactly
.favorites-section {
  margin-bottom: 0 !important;
  padding-bottom: 0;
  overflow-y: hidden;
  font-size: 1.1rem;
  font-family: inherit;
  font-weight: 400;

  .favorites-group {
    // Main group link (matches .navigation > li > a styling)
    > a {
      display: block;
      text-overflow: ellipsis;
      overflow: hidden;
      color: #b4b7bd;
      padding: 10px 15px;
      margin: 0 15px;
      line-height: 1.45;
      transition: all 0.5s ease, background 0s, color 0s;

      .favorites-icon {
        color: #ffc107;
        height: 20px;
        width: 20px;
        position: relative;
        top: 1px;
        margin-right: 1.38rem;
      }

      .menu-title {
        font-weight: 400;
      }

      // Hover transform effect
      &:hover > * {
        transition: transform 0.25s ease;
        transform: translateX(5px);
      }

      // Chevron arrow
      &:after {
        content: '';
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236e6b7b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-chevron-right'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 1rem;
        height: 1rem;
        width: 1rem;
        display: inline-block;
        position: absolute;
        right: 20px;
        top: 14px;
        transform: rotate(0deg);
        transition: all 0.2s ease-out;
      }
    }

    // Open state - matches .navigation > li.open > a
    &.open:not(.menu-item-closing) > a,
    &.sidebar-group-active > a {
      color: darken(#b4b7bd, 10%);
      background: darken(#283046, 4%);
      border-radius: 6px;

      &:after {
        transform: rotate(90deg);
      }
    }

    // Sub-items container
    .menu-content.sub-items {
      padding: 0;
      margin: 0;
    }
  }

  // Favourite items - matches .navigation > li > ul li styling
  .favorite-item {
    position: relative;
    white-space: nowrap;
    color: #b4b7bd;
    background: transparent;

    // First child margin
    &:first-of-type {
      margin-top: 7px !important;
    }

    // Item margin like other sub-items
    margin: 0 15px;

    // Remove star trigger - appears on hover
    .favorite-remove-trigger {
      position: absolute;
      left: 8px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.2s ease;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;

      .favorite-remove-icon {
        color: #ffc107;
        fill: #ffc107;
        transition: color 0.2s ease, transform 0.2s ease;
      }

      &:hover .favorite-remove-icon {
        transform: scale(1.2);
        color: #ea5455;
        fill: transparent;
      }
    }

    &:hover .favorite-remove-trigger {
      opacity: 1;
    }

    // Hide the item's icon when showing remove star
    &:hover a svg:first-of-type {
      opacity: 0;
    }

    a {
      display: block;
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 10px 15px 10px 30px;
      line-height: 1.45;
      color: #b4b7bd;

      svg {
        height: 11px;
        width: 11px;
        font-size: 11px;
        margin-right: 1.38rem;
        transition: opacity 0.2s ease;
      }

      // Filled yellow star for favorite items
      .favorite-star-filled {
        color: #ffc107;
        fill: #ffc107;
      }

      // Hover transform effect
      &:hover > * {
        transition: transform 0.25s ease;
        transform: translateX(5px);
      }
    }

    .pinned-indicator {
      color: #ffc107;
    }

    // Hover state - matches normal menu exactly
    &:not(.active) > a:hover {
      background: color-mix(in srgb, var(--primary) 8%, transparent);
      border-radius: 6px;
    }

    // Active state - matches .navigation .active li
    &.active {
      background: linear-gradient(118deg, var(--primary), color-mix(in srgb, var(--primary) 70%, transparent));
      box-shadow: 0 0 10px 1px color-mix(in srgb, var(--primary) 70%, transparent);
      border-radius: 4px;
      z-index: 1;

      > a {
        color: #fff;
      }
    }
  }
}

.favorites-separator {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  margin: 0.75rem 1rem;
}

// Light theme adjustments
.light-layout {
  .favorites-separator {
    background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent);
  }

  // Favourites section light mode
  .favorites-section {
    .favorites-group {
      > a {
        color: #6e6b7b;

        .favorites-icon {
          color: #ffc107;
        }

        &:after {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236e6b7b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-chevron-right'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
        }
      }

      &.open:not(.menu-item-closing) > a,
      &.sidebar-group-active > a {
        color: darken(#6e6b7b, 10%);
        background: darken(#fff, 6%);
      }
    }

    .favorite-item {
      color: #6e6b7b;

      .favorite-remove-trigger .favorite-remove-icon {
        color: #ffc107;
        fill: #ffc107;
      }

      a {
        color: #6e6b7b;

        .favorite-star-filled {
          color: #ffc107;
          fill: #ffc107;
        }
      }

      &:hover > a {
        color: darken(#6e6b7b, 10%);
      }

      &.active {
        background: linear-gradient(118deg, var(--primary), color-mix(in srgb, var(--primary) 70%, transparent));
        box-shadow: 0 0 10px 1px color-mix(in srgb, var(--primary) 70%, transparent);

        > a {
          color: #fff;

          .favorite-star-filled {
            color: #ffc107;
            fill: #ffc107;
          }
        }
      }
    }
  }
}
</style>
